import { NumberFormatPreset } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  PurchaseRequisitionDataGridColumnGroup,
  PurchaseRequisitionDataGridField,
  PurchaseRequisitionDataGridRow,
} from '~/common/types';
import {
  ActiveCell,
  cellFactory,
  ChipCell,
  CurrencyCell,
  DataGridConfig,
  DataGridLinkVariant,
  GridAlignmentType,
  gridColDefFactory,
  gridColumnGroupFactory,
  headerGroupWithIcon,
  LinkCell,
  LinkCellValue,
  ListCountCell,
  numberFormatterFactory,
  TooltipCell,
  UseDataGridConfig,
} from '~/components/DataGridCustom';
import { commodityGroupValueGetter } from '~/components/DataGridWidget/DataGridWidget.util';
import { IconId } from '~/components/Icon';
import { ListCountCellMenu } from '~/components/ListCountCellMenu/ListCountCellMenu';
import { SourceLinksButton } from '~/components/SourceLinksButton/SourceLinksButton';
import { RouteId } from '~/routes';
import { notEmpty } from '~/util/array';

export const usePurchaseRequisitionDataGridConfig: UseDataGridConfig<PurchaseRequisitionDataGridRow> = () => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(undefined, { keyPrefix: 'Base.purchaseRequisition.columns' });
  const { t: tGroup } = useTranslation(undefined, { keyPrefix: 'Base.purchaseRequisition.columnBands' });

  return useMemo(() => {
    const createColumn = gridColDefFactory({ tColumn, t });
    const createGroup = gridColumnGroupFactory({ tGroup });
    return {
      sortModel: undefined,
      columnVisibilityModel: {},
      columns: [
        createColumn({
          field: PurchaseRequisitionDataGridField.EXTERNAL_ID,
          renderCell: cellFactory(LinkCell),
          valueGetter: (value, row): LinkCellValue => ({
            variant: DataGridLinkVariant.PRIMARY,
            to: RouteId.purchaseRequisitionDetail(row.id),
            label: value,
          }),
          valueFormatter: (value: { label: string }) => value?.label,
          minWidth: 250,
          hideable: false,
        }),
        createColumn({
          field: PurchaseRequisitionDataGridField.DESCRIPTION,
          renderCell: cellFactory(TooltipCell),
          minWidth: 350,
        }),
        createColumn({
          field: PurchaseRequisitionDataGridField.TYPE,
          valueFormatter: (value: PurchaseRequisitionDataGridRow['type']) =>
            t(`Base.purchaseRequisition.status.${value}`),
          minWidth: 100,
        }),
        createColumn({
          field: PurchaseRequisitionDataGridField.LINE_ITEMS_COUNT,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.INTEGER }),
          minWidth: 150,
        }),
        createColumn({
          field: PurchaseRequisitionDataGridField.PREFERRED_SUPPLIERS_COUNT,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.INTEGER }),
          minWidth: 150,
        }),
        createColumn({
          field: PurchaseRequisitionDataGridField.FIXED_SUPPLIERS_COUNT,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.INTEGER }),
          minWidth: 150,
        }),
        createColumn({
          field: PurchaseRequisitionDataGridField.TOTAL_VALUE,
          renderCell: cellFactory(CurrencyCell),
          minWidth: 200,
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: PurchaseRequisitionDataGridField.IS_BLOCKED,
          renderCell: cellFactory(ActiveCell),
          minWidth: 150,
        }),
        createColumn({
          field: PurchaseRequisitionDataGridField.PRIMARY_COMMODITY_GROUP,
          renderCell: cellFactory(ChipCell, ({ value }) => ({ value: commodityGroupValueGetter(value) })),
          sortable: false,
          minWidth: 120,
        }),
        createColumn({
          field: PurchaseRequisitionDataGridField.COMMODITY_GROUP_COUNT,
          renderCell: cellFactory(ListCountCell, ({ row }) => ({
            renderList: notEmpty(row.commodityGroupList) && (() => <ListCountCellMenu list={row.commodityGroupList} />),
          })),
          minWidth: 100,
        }),
        createColumn({
          field: PurchaseRequisitionDataGridField.LINKS,
          renderCell: cellFactory(SourceLinksButton, ({ value }) => ({ links: value })),
          minWidth: 120,
          sortable: false,
        }),
      ],
      columnGroupingModel: [
        createGroup({
          groupId: PurchaseRequisitionDataGridColumnGroup.PURCHASE_REQUISITION,
          renderHeaderGroup: headerGroupWithIcon(IconId.purchaseRequisition),
          children: [
            //
            { field: PurchaseRequisitionDataGridField.EXTERNAL_ID },
            { field: PurchaseRequisitionDataGridField.DESCRIPTION },
            { field: PurchaseRequisitionDataGridField.TYPE },
            { field: PurchaseRequisitionDataGridField.LINE_ITEMS_COUNT },
            { field: PurchaseRequisitionDataGridField.TOTAL_VALUE },
            { field: PurchaseRequisitionDataGridField.IS_BLOCKED },
            { field: PurchaseRequisitionDataGridField.PRIMARY_COMMODITY_GROUP },
            { field: PurchaseRequisitionDataGridField.COMMODITY_GROUP_COUNT },
          ],
        }),
        createGroup({
          groupId: PurchaseRequisitionDataGridColumnGroup.SUPPLIER,
          renderHeaderGroup: headerGroupWithIcon(IconId.supplier),
          children: [
            //
            { field: PurchaseRequisitionDataGridField.PREFERRED_SUPPLIERS_COUNT },
            { field: PurchaseRequisitionDataGridField.FIXED_SUPPLIERS_COUNT },
          ],
        }),
      ],
      exportModel: {
        [PurchaseRequisitionDataGridField.LINKS]: {
          disable: true,
        },
      },
    } as DataGridConfig<PurchaseRequisitionDataGridRow>;
  }, [t, tColumn, tGroup]);
};
